import React from "react";
import { useLocation, useNavigate } from "react-router";
import {
  ArrowRight,
  Factory,
  Truck,
  UsersRound,
  Warehouse,
} from "lucide-react";
import {
  getSupabaseSession,
  isSupabaseConfigured,
  signOutSupabaseSession,
} from "../lib/supabaseClient.js";
import { resolveAuthenticatedEntryPath } from "../lib/driverPortal.js";
import { BRAND_LOGO_SRC, BRAND_NAME } from "./BrandLogo.jsx";
import LandscapeSlideshow from "./LandscapeSlideshow.jsx";
import PublicFeatureBanner from "./PublicFeatureBanner.jsx";
import "./PublicLandingPage.css";

const audiences = [
  { label: "Transporteurs", icon: Truck },
  { label: "Distributeurs", icon: Warehouse },
  { label: "Industriels", icon: Factory },
  { label: "Opérateurs logistiques", icon: UsersRound },
];

const steps = [
  {
    description: "Clients, volumes, produits et contraintes de compartiments.",
    step: "01",
    title: "Importez",
  },
  {
    description: "Le moteur arbitre la flotte selon capacités, règles et priorités métier.",
    step: "02",
    title: "Optimisez",
  },
  {
    description: "Suivez les routes, les écarts et l’exécution terrain.",
    step: "03",
    title: "Exécutez",
  },
  {
    description: "Consultez marges, coûts et résultats consolidés.",
    step: "04",
    title: "Analysez",
  },
];

// Like BrandLogo, use Vite's public base in development and FastAPI's brand mount otherwise.
const landingAssetBase = import.meta.env.DEV
  ? `${import.meta.env.BASE_URL}brand/landing/`
  : "/brand/landing/";

const floatingSignals = [
  { id: "warehouse", delay: "0s", label: "Entrepôt : 48,2 km" },
  { id: "loading", delay: "0.35s", label: "Chargement : 14,0 t" },
  { id: "delivery", delay: "0.7s", label: "Livraison : 21 min" },
];

function resolvePublicLandingNotice(search = "") {
  const searchParams = new URLSearchParams(typeof search === "string" ? search : "");
  const authNotice = typeof searchParams.get("authNotice") === "string"
    ? searchParams.get("authNotice").trim().toLowerCase()
    : "";

  if (authNotice === "session-expired") {
    return {
      description: "Votre session a expiré. Vous avez été redirigé vers l’accueil public pour reprendre la navigation ou vous reconnecter.",
      title: "Session expirée",
      toneClassName: "border-amber-200/80 bg-amber-50/90 text-amber-900",
    };
  }

  if (authNotice === "signed-out") {
    return {
      description: "Vous avez été déconnecté de l’espace d’optimisation. Vous pouvez rester sur l’accueil public ou vous reconnecter quand vous le souhaitez.",
      title: "Déconnexion effectuée",
      toneClassName: "border-emerald-200/70 bg-white/70 text-emerald-900",
    };
  }

  if (authNotice === "auth-required") {
    return {
      description: "L’espace d’optimisation nécessite une session active. Vous pouvez découvrir la plateforme ici puis vous reconnecter quand vous le souhaitez.",
      title: "Connexion requise",
      toneClassName: "border-emerald-200/70 bg-white/70 text-emerald-900",
    };
  }

  return null;
}

export default function PublicLandingPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const landingNotice = React.useMemo(() => resolvePublicLandingNotice(location.search), [location.search]);
  const shouldStayOnLanding = React.useMemo(() => {
    const searchParams = new URLSearchParams(location.search || "");
    return searchParams.get("fromApp") === "1" || Boolean(searchParams.get("authNotice"));
  }, [location.search]);
  const shouldClearSessionOnLanding = React.useMemo(() => {
    const searchParams = new URLSearchParams(location.search || "");
    const authNotice = (searchParams.get("authNotice") || "").trim().toLowerCase();
    return authNotice === "signed-out" || authNotice === "session-expired";
  }, [location.search]);

  React.useEffect(() => {
    if (typeof document !== "undefined") {
      document.body.classList.remove("page-shell", "results-page");
      document.body.style.background = "";
      document.documentElement.style.background = "";
    }

    let isActive = true;
    if (!isSupabaseConfigured()) {
      return () => {
        isActive = false;
      };
    }

    if (shouldStayOnLanding) {
      if (shouldClearSessionOnLanding) {
        void signOutSupabaseSession({ scope: "local" }).catch(() => {});
      }
      return () => {
        isActive = false;
      };
    }

    void getSupabaseSession()
      .then(({ session }) => {
        if (isActive && session) {
          void resolveAuthenticatedEntryPath().then((nextPath) => {
            if (!isActive) {
              return;
            }

            navigate(nextPath, { replace: true });
          });
        }
      })
      .catch(() => {});

    return () => {
      isActive = false;
    };
  }, [navigate, shouldClearSessionOnLanding, shouldStayOnLanding]);

  const currentYear = new Date().getFullYear();

  return (
    <div className="public-landing public-landing--immersive min-h-screen text-slate-900">
      <header className="public-landing__header">
        <div className="mx-auto flex w-full max-w-[100rem] items-center justify-end gap-4 px-10 py-4 max-[720px]:px-5">
          <div className="flex items-center gap-3 max-[900px]:gap-2">
            <nav className="flex items-center gap-6 text-[0.82rem] font-medium tracking-[0.01em] text-slate-600 max-[900px]:hidden" aria-label="Navigation publique">
              <a className="transition hover:text-accent" href="#fonctionnalites">Fonctionnalités</a>
            </nav>

            <a className="public-landing__ghost-btn" href="/login">
              Se connecter
            </a>
          </div>
        </div>
      </header>

      <main>
        <div className="public-landing__home-screen">
          <section className="public-landing__hero">
            <div className="public-landing__landscape" aria-hidden="true">
              <LandscapeSlideshow />
            </div>
            <div className="public-landing__hero-copy">
              {landingNotice ? (
                <div role="status" className={`mb-6 inline-flex max-w-xl items-start gap-3 rounded-[4px] border px-4 py-3 text-left shadow-[0_10px_30px_rgba(15,40,35,0.05)] backdrop-blur-md ${landingNotice.toneClassName}`}>
                  <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-[4px] border border-current/15 bg-white/60 text-[0.72rem] font-black">
                    i
                  </span>
                  <div className="grid gap-1">
                    <p className="m-0 text-[0.7rem] font-bold uppercase tracking-[0.14em]">{landingNotice.title}</p>
                    <p className="m-0 text-sm leading-6 text-current/90">{landingNotice.description}</p>
                  </div>
                </div>
              ) : null}

              <div className="public-landing__wordmark">
                <img src={BRAND_LOGO_SRC} alt={BRAND_NAME} width={535} height={165} decoding="async" />
              </div>

              <h1 className="public-landing__headline font-display">
                <span className="block">Des décisions</span>
                <span className="block">plus intelligentes</span>
                <span className="block">pour un transport</span>
                <span className="block">plus performant.</span>
              </h1>

              <p className="public-landing__description">
                Optimisez vos opérations&nbsp;:
                {" "}moins de coûts, plus d’efficacité, une flotte toujours en mouvement.
              </p>

              <div className="public-landing__actions">
                <a className="public-landing__primary-btn" href="/login">
                  Se connecter à {BRAND_NAME}
                  <span className="public-landing__primary-btn-icon" aria-hidden="true">
                    <ArrowRight size={16} />
                  </span>
                </a>
                <a
                  className="public-landing__ghost-btn"
                  href={`${landingAssetBase}transia-promo.mp4`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Voir une démo
                </a>
              </div>

              <div className="public-landing__audiences">
                <p id="landing-audiences-label" className="public-landing__audiences-label">Conçu pour</p>
                <ul className="public-landing__audience-list" aria-labelledby="landing-audiences-label">
                  {audiences.map(({ label, icon: Icon }) => (
                    <li key={label}>
                      <Icon size={28} strokeWidth={2.1} aria-hidden="true" />
                      <span>{label}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="public-landing__hero-stage" role="group" aria-label="Exemple illustratif d’un parcours de transport">
              <svg className="public-landing__signal-route" viewBox="0 0 720 320" fill="none" aria-hidden="true" focusable="false">
                <path className="public-landing__route-desktop" d="M 122 134 C 230 134 230 204 360 204 S 494 272 598 272" />
                <path className="public-landing__route-tablet" d="M 122 134 C 230 134 230 159 360 159 S 494 185 598 185" />
              </svg>
              {floatingSignals.map((signal) => (
                <span
                  key={signal.id}
                  className={`public-landing__signal public-landing__signal--${signal.id}`}
                  style={{ animationDelay: signal.delay }}
                >
                  <img
                    src={`${landingAssetBase}${signal.id}.png`}
                    alt={`${signal.label} — illustration`}
                    width={1774}
                    height={887}
                    decoding="async"
                    draggable={false}
                  />
                </span>
              ))}
            </div>
          </section>

          <PublicFeatureBanner id="fonctionnalites" />
        </div>

        <section id="comment-ca-marche" className="relative mx-auto w-full max-w-[88rem] px-8 py-16 max-[720px]:px-4 max-[720px]:py-12">
          <div>
            <p className="text-[0.7rem] font-bold uppercase tracking-[0.18em] text-[#0f5c57]">Comment ça marche</p>
            <h2 className="mt-3 font-display text-[clamp(1.55rem,2.6vw,2.35rem)] font-bold whitespace-nowrap text-slate-950 max-[720px]:whitespace-normal">
              Quatre étapes, de la commande à la livraison.
            </h2>
          </div>

          <ol className="mt-10 grid gap-3 min-[900px]:grid-cols-4">
            {steps.map((step) => (
              <li key={step.step} className="public-landing__step">
                <span className="font-display text-3xl font-bold text-[#0f5c57]">{step.step}</span>
                <h3 className="mt-4 text-base font-semibold text-slate-950">{step.title}</h3>
                <p className="mt-2 text-sm leading-6 text-slate-600">{step.description}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className="relative mx-auto w-full max-w-[88rem] px-8 pb-14 max-[720px]:px-4 max-[720px]:pb-10">
          <div className="public-landing__cta">
            <h2 className="font-display text-[clamp(1.9rem,3.5vw,2.9rem)] font-bold">
              Prêt à optimiser votre flotte ?
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-base leading-7 text-white/80">
              Connectez-vous et lancez votre première optimisation. L&apos;accueil reste public ; l&apos;espace métier s&apos;ouvre au moment de la connexion.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <a className="public-landing__cta-primary" href="/login">
                Se connecter à {BRAND_NAME}
                <ArrowRight size={15} aria-hidden="true" />
              </a>
              <a className="public-landing__cta-secondary" href="#fonctionnalites">
                Voir le fonctionnement
              </a>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-slate-200/80 bg-white/55 backdrop-blur-md">
        <div className="border-t border-slate-200/70">
          <div className="mx-auto flex w-full max-w-[88rem] flex-col gap-2 px-8 py-4 text-sm text-slate-500 min-[900px]:flex-row min-[900px]:items-center min-[900px]:justify-between max-[720px]:px-4">
            <p>© {currentYear} {BRAND_NAME}. Tous droits réservés.</p>
            <p>Présentation publique · accès métier via connexion contrôlée.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
